// Dependency Graph types and Zod schemas (ARCHITECTURE.md §4.5).
//
// The Dependency Graph layers project-level structure (modules, packages,
// artifacts) on top of the language-agnostic AST Graph (§4.4). It embeds the
// underlying `AstGraph`, adds artifact-aware nodes, and records which artifacts
// each file participates in via `artifactMapping`.
//
// As elsewhere in the codebase, the inferred Zod types are the compile-time
// source of truth; the schemas provide runtime validation at trust boundaries
// (cache deserialization, plugin input).

import { z } from "zod";

import { GraphEdgeSchema, GraphNodeSchema } from "../types.js";
import { AstGraphSchema } from "../ast/types.js";

// ---------------------------------------------------------------------------
// DependencyNode
// ---------------------------------------------------------------------------

// DependencyNode extends GraphNode (§4.1). Unlike AstNode, all structural kinds
// are valid here, and every node records the artifact IDs it belongs to. Symbol
// nodes (§5) are carried through so symbol-level impact traversal can resolve
// them; a symbol inherits the artifact ids of its containing file.
export const DependencyNodeSchema = GraphNodeSchema.extend({
  kind: z.enum(["file", "symbol", "module", "package", "artifact"]),
  artifactIds: z.array(z.string()),
});

export type DependencyNode = z.infer<typeof DependencyNodeSchema>;

// ---------------------------------------------------------------------------
// DependencyGraph (in-memory)
// ---------------------------------------------------------------------------

// The canonical in-memory representation uses native Maps, matching the
// interface in §4.5. `z.map()` validates the live Map instances; this is the
// schema to use when checking a DependencyGraph that already exists in memory.
export const DependencyGraphSchema = z.object({
  ast: AstGraphSchema,
  nodes: z.map(z.string(), DependencyNodeSchema),
  edges: z.array(GraphEdgeSchema),
  // filePath → artifact IDs
  artifactMapping: z.map(z.string(), z.array(z.string())),
});

export type DependencyGraph = z.infer<typeof DependencyGraphSchema>;
